import { useEffect, useRef, useState } from 'react';
import {
  getRequiredRemovalOptions,
  removeExperienceFromDays,
} from '../../logic/removeExperienceFromDays';

interface RemovalParams {
  dayIndex: number;
  experience: any;
  experienceStartIndex: number;
  days: any[];
  experiencesMap: Record<string, any>;
  updateDays: (days: any[]) => void;
  onExperienceRemoved?: (info: any) => void;
  registerRemoveHandler?: (...args: any[]) => void;
  isExperienceFirstDay: boolean;
  readOnly: boolean;
  onEvent?: (name: string, payload: unknown) => void;
}

// Owns the "remove this experience instance" flow: the required-options
// confirmation, the actual day-range cleanup, and registering the handler
// the frame's own removal cross calls into (KD-3581 §3 / AC09).
export function useTripDayExperienceRemoval({
  dayIndex,
  experience,
  experienceStartIndex,
  days,
  experiencesMap,
  updateDays,
  onExperienceRemoved,
  registerRemoveHandler,
  isExperienceFirstDay,
  readOnly,
  onEvent,
}: RemovalParams) {
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [requiredExtras, setRequiredExtras] = useState<string[]>([]);
  const [previousExperience, setPreviousExperience] = useState<any>(null);

  const removeExperience = () => {
    // AC09 — the trip is photographed before anything is touched. On to the
    // additional services live on the days themselves, so the days alone carry
    // the whole selection back if the user undoes the removal.
    onExperienceRemoved?.({
      startDayIndex: experienceStartIndex,
      // The undo offer stands on the days the experience covered, so it needs
      // their count: a 0-day experience still occupies its own day.
      dayCount: Math.max(experience?.durationDays ?? 0, 1),
      experienceName: experience?.name ?? '',
      snapshot: { days },
    });

    updateDays(removeExperienceFromDays({ days, dayIndex, experience }));

    onEvent?.('experience.removed', {
      experienceId: experience?.id,
      experienceName: experience?.name,
      dayIndex,
    });
  };

  const handleRemoveExperience = () => {
    const { prevProgram, requiredOptions } = getRequiredRemovalOptions({
      dayIndex,
      experience,
      days,
      experiencesMap,
    });

    if (requiredOptions.length > 0 && prevProgram) {
      setPreviousExperience(prevProgram);
      setRequiredExtras(requiredOptions);
      setShowRemoveConfirmation(true);
    } else {
      removeExperience();
    }
  };

  const confirmRemoveExperience = () => {
    setShowRemoveConfirmation(false);
    removeExperience();
  };

  // KD-3581 §3 / AC09 — the removal cross moved to the frame header, which
  // knows nothing about the required-options warning this day owns. So the day
  // lends its handler to the frame instead of the frame reimplementing it, and
  // only the first day of an instance registers: that is the one that knows the
  // whole range to clear.
  const removeExperienceRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    removeExperienceRef.current = handleRemoveExperience;
  });

  const canRemoveExperience = isExperienceFirstDay && !readOnly;

  useEffect(() => {
    if (!registerRemoveHandler || !canRemoveExperience) {
      return undefined;
    }

    const startDayIndex = experienceStartIndex;
    registerRemoveHandler(startDayIndex, () => removeExperienceRef.current?.());

    return () => registerRemoveHandler(startDayIndex, null);
  }, [registerRemoveHandler, canRemoveExperience, experienceStartIndex]);

  return {
    showRemoveConfirmation,
    setShowRemoveConfirmation,
    confirmRemoveExperience,
    requiredExtras,
    previousExperience,
  };
}
